import React, { useState } from "react";
import {
  TextInput,
  List,
  SimpleList,
  ReferenceField,
  Datagrid,
  TextField,
  EmailField,
  DateField,
  EditButton,
  DeleteButton,
  useTranslate,
  useListContext,
  usePermissions,
  ReferenceInput,
  SelectInput,
  FunctionField,
} from "react-admin";
import { useMediaQuery, Box, Typography, Paper, Chip, Button } from "@mui/material";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { Link as RouterLink } from "react-router-dom";
import { useFeaturePreferences } from "../../config/features";
import { UserImportModal } from "./UserImportModal";

const filterInputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fcf9f4",
    borderRadius: "2px",
    fontFamily: "'Inter', sans-serif",
    "& fieldset": {
      borderColor: "rgba(3, 37, 23, 0.25)",
    },
    "&:hover fieldset": {
      borderColor: "#1b3b2b",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#c29b38",
    },
  },
};

const userFilters = [
  <TextInput
    key="search"
    label="Search"
    source="firstname@_like"
    alwaysOn
    sx={filterInputStyles}
  />,
];

const adminUserFilters = [
  <TextInput
    key="search"
    label="Search"
    source="firstname@_like"
    alwaysOn
    sx={filterInputStyles}
  />,
  <ReferenceInput
    key="nutritionist_filter"
    label="Filtrar por Especialista"
    source="nutritionist_id"
    reference="nutritionist"
    sx={{ minWidth: 200, ...filterInputStyles }}
  >
    <SelectInput
      optionText={(r) => `${r.firstname || ""} ${r.lastname || ""}`}
    />
  </ReferenceInput>,
];

const UserListHeader = ({ onOpenImport }) => {
  const translate = useTranslate();
  const { total, data } = useListContext();
  const { permissions } = usePermissions();
  const count = total !== undefined ? total : (data ? Object.keys(data).length : 0);
  const isNutritionist = permissions?.role === "nutritionist";

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 2.5,
        backgroundColor: "#faf7f2",
        border: "1px solid rgba(3, 37, 23, 0.18)",
        borderTop: "4px solid #1b3b2b",
        borderRadius: "2px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            backgroundColor: "#1b3b2b",
            color: "#fed269",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid #c29b38",
          }}
        >
          <FolderSharedIcon fontSize="small" />
        </Box>
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontWeight: 700,
              color: "#032517",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {translate("user_list.title", { _: "Registro Histórico de Sujetos" })}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: "'Inter', sans-serif",
              color: "#424843",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontSize: "11px",
              display: "block",
              mt: 0.3,
            }}
          >
            {translate("user_list.subtitle", {
              _: "Catálogo Oficial de Expedientes Biométricos & Atletas",
            })}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {onOpenImport && (
          <Button
            variant="outlined"
            size="small"
            onClick={onOpenImport}
            startIcon={<UploadFileIcon />}
            sx={{
              borderColor: "#c29b38",
              color: "#032517",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "none",
              backgroundColor: "#fff",
              "&:hover": {
                borderColor: "#032517",
                backgroundColor: "rgba(194, 155, 56, 0.15)",
              },
            }}
          >
            {translate("import_csv.button", { _: "Importar CSV" })}
          </Button>
        )}
        {isNutritionist && (
          <Chip
            icon={<LocalPharmacyIcon fontSize="small" sx={{ color: "#1b3b2b !important" }} />}
            label={translate("auth.specialist_assigned", {
              _: "Asignado a su consulta profesional",
            })}
            size="small"
            sx={{
              backgroundColor: "rgba(27, 59, 43, 0.08)",
              border: "1px solid rgba(27, 59, 43, 0.3)",
              color: "#1b3b2b",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: "11.5px",
            }}
          />
        )}
        <Chip
          label={translate("user_list.total_badge", {
            count,
            _: `Expedientes Archivados: ${count}`,
          })}
          size="small"
          sx={{
            backgroundColor: "rgba(194, 155, 56, 0.15)",
            color: "#775a00",
            border: "1px solid rgba(194, 155, 56, 0.4)",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            fontSize: "12px",
            px: 1,
            py: 0.5,
            borderRadius: "2px",
          }}
        />
      </Box>
    </Paper>
  );
};

const UserListLedgerFooter = () => {
  const translate = useTranslate();
  const { total, data } = useListContext();
  const count = total !== undefined ? total : (data ? Object.keys(data).length : 0);

  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        backgroundColor: "#faf7f2",
        borderTop: "2px solid #c29b38",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontFamily: "'EB Garamond', Georgia, serif",
          fontStyle: "italic",
          fontSize: "13.5px",
          color: "#424843",
          letterSpacing: "0.02em",
        }}
      >
        {translate("user_list.ledger_folio", {
          _: "Registro Oficial del Cuaderno Antropométrico • Folio de Archivo Activo",
        })}
      </Typography>
      <Chip
        label={translate("user_list.active_entries", {
          count,
          _: `Total Registrados: ${count}`,
        })}
        size="small"
        sx={{
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 700,
          fontSize: "11.5px",
          backgroundColor: "#fff",
          border: "1px solid rgba(27, 59, 43, 0.3)",
          color: "#032517",
          borderRadius: "2px",
        }}
      />
    </Box>
  );
};

export const UserList = (props) => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { isFeatureEnabled } = useFeaturePreferences();
  const { permissions } = usePermissions();
  const [importOpen, setImportOpen] = useState(false);

  const isNutritionist = permissions?.role === "nutritionist";
  const permanentFilter =
    isNutritionist && permissions?.nutritionistId
      ? { nutritionist_id: permissions.nutritionistId }
      : undefined;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <List
        {...props}
        filters={permissions?.role === "admin" ? adminUserFilters : userFilters}
        filter={permanentFilter}
        component="div"
      >
        <UserListHeader onOpenImport={() => setImportOpen(true)} />
        {isSmall ? (
          <SimpleList
            primaryText={(record) => `${record.firstname || ""} ${record.lastname || ""}`}
            linkType="show"
            secondaryText={(record) => record.email || record.phone || ""}
            tertiaryText={(record) => record.birthday || ""}
            rowStyle={(record) => ({
              backgroundColor: "#fcf9f4",
              border: "1px solid rgba(3, 37, 23, 0.18)",
              borderLeft:
                record.gender === "male" || record.gender === true
                  ? "4px solid #1b3b2b"
                  : "4px solid #c29b38",
              borderRadius: "3px",
              margin: "10px 0",
              padding: "12px 14px",
              boxShadow: "0 1px 4px rgba(27, 59, 43, 0.05)",
            })}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              border: "2px solid #1b3b2b",
              borderRadius: "3px",
              overflow: "hidden",
              backgroundColor: "#fff",
              boxShadow: "0 2px 10px rgba(27, 59, 43, 0.08)",
            }}
          >
            <Box
              className="vintage-table-container"
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Datagrid
                rowClick="show"
                sx={{
                  width: "100%",
                  minWidth: 1050,
                  boxShadow: "none",
                "& .MuiTableCell-head": {
                  fontWeight: 700,
                  backgroundColor: "#1b3b2b",
                  color: "#fcf9f4",
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: "14.5px",
                  letterSpacing: "0.05em",
                  borderBottom: "2px solid #c29b38",
                  borderRight: "1px solid rgba(252, 249, 244, 0.12)",
                  padding: "12px 14px",
                  textTransform: "uppercase",
                  "& .MuiTableSortLabel-root": {
                    color: "#fcf9f4 !important",
                    "& .MuiTableSortLabel-icon": {
                      color: "#fed269 !important",
                    },
                  },
                },
                "& .MuiTableCell-body": {
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "13px",
                  color: "#1c1c19",
                  padding: "10px 14px",
                  borderBottom: "1px solid rgba(27, 59, 43, 0.09)",
                  borderRight: "1px solid rgba(27, 59, 43, 0.05)",
                  verticalAlign: "middle",
                },
                "& .MuiTableRow-root:hover": {
                  backgroundColor: "rgba(245, 239, 230, 0.95) !important",
                  borderLeft: "4px solid #c29b38",
                },
                "& .MuiTableRow-root:nth-of-type(even)": {
                  backgroundColor: "#faf7f2",
                },
                "& .column-id span": {
                  display: "inline-block",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: 700,
                  fontSize: "12px",
                  backgroundColor: "#fcf9f4",
                  border: "1px solid #1b3b2b",
                  boxShadow: "1px 1px 0px rgba(27, 59, 43, 0.7)",
                  px: 0.8,
                  py: 0.2,
                  borderRadius: "1px",
                  color: "#032517",
                },
                "& .column-phone, & .column-birthday, & .column-created_at, & .column-updated_at": {
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "12.5px",
                },
                "& .column-gender span": {
                  display: "inline-block",
                  px: 1,
                  py: 0.2,
                  borderRadius: "1px",
                  fontSize: "11px",
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  border: "1px solid rgba(194, 155, 56, 0.5)",
                  backgroundColor: "rgba(194, 155, 56, 0.12)",
                  color: "#775a00",
                },
              }}
            >
              <TextField source="id" />
              <TextField source="firstname" />
              <TextField source="lastname" />
              <EmailField source="email" />
              <TextField source="phone" />
              <TextField source="address" />
              <TextField source="birthday" />
              <TextField source="gender" />
              {isFeatureEnabled("nutritionist") && (
                <ReferenceField source="nutritionist_id" reference="nutritionist" />
              )}
              <DateField source="created_at" />
              <FunctionField
                label="Evaluación"
                render={(record) => (
                  <Button
                    component={RouterLink}
                    to={`/measurement/create?user_id=${record.id}`}
                    variant="contained"
                    size="small"
                    startIcon={<PostAddIcon fontSize="small" />}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      backgroundColor: "#1b3b2b",
                      color: "#fed269",
                      fontFamily: "'EB Garamond', Georgia, serif",
                      fontWeight: 700,
                      fontSize: "12.5px",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      py: 0.3,
                      px: 1.2,
                      border: "1px solid #c29b38",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#032517",
                        color: "#fff",
                      },
                    }}
                  >
                    + Medición
                  </Button>
                )}
              />
              <EditButton />
              <DeleteButton />
            </Datagrid>
          </Box>
          <UserListLedgerFooter />
        </Paper>
        )}
      </List>

      <UserImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
    </Box>
  );
};

